import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { callRpcResult } from "@/lib/api/rpc";

/**
 * Org-scoped template assignment persistence (Supabase).
 *
 * Replaces the Phase-1 localStorage placeholder. Account defaults live on
 * `connectors.listing_template_id`; per-listing overrides live on
 * `channel_listings.listing_template_override`. Writes go through
 * SECURITY DEFINER RPCs that enforce org membership + channel compatibility
 * server-side (migration 0195), so the browser cannot persist a cross-org or
 * incompatible assignment.
 */

type AssignmentMap = Record<string, string>;

/**
 * Account-level default template per connector, scoped to the organisation.
 * `assignments` is keyed by connector id; absent keys mean "no default yet"
 * (resolution falls back to the platform default).
 */
export function useAccountTemplateAssignments(orgId: string | null | undefined) {
  const queryClient = useQueryClient();

  const { data: assignments = {}, isLoading } = useQuery({
    queryKey: ["template-assignments", orgId],
    enabled: !!orgId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("connectors")
        .select("id, listing_template_id")
        .eq("org_id", orgId!);
      if (error) throw error;
      const map: AssignmentMap = {};
      for (const row of (data ?? []) as { id: string; listing_template_id: string | null }[]) {
        if (row.listing_template_id) map[row.id] = row.listing_template_id;
      }
      return map;
    },
  });

  const assign = useCallback(
    async (connectorId: string, templateId: string | null) => {
      await callRpcResult("set_connector_listing_template", {
        p_org_id: orgId,
        p_connector_id: connectorId,
        p_template_id: templateId,
      });
      await queryClient.invalidateQueries({ queryKey: ["template-assignments", orgId] });
    },
    [orgId, queryClient],
  );

  const clear = useCallback(
    async (connectorId: string) => {
      await assign(connectorId, null);
    },
    [assign],
  );

  return { assignments, assign, clear, isLoading };
}

/**
 * Per-listing template override. `override` is the template id, or `null` when
 * the listing follows its account default.
 */
export function useListingTemplateOverride(
  orgId: string | null | undefined,
  listingId: string | null | undefined,
) {
  const queryClient = useQueryClient();

  const { data: override = null, isLoading } = useQuery({
    queryKey: ["listing-template-override", orgId, listingId],
    enabled: !!orgId && !!listingId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("channel_listings")
        .select("listing_template_override")
        .eq("org_id", orgId!)
        .eq("id", listingId!)
        .maybeSingle();
      if (error) throw error;
      return (data as { listing_template_override: string | null } | null)?.listing_template_override ?? null;
    },
  });

  const set = useCallback(
    async (templateId: string | null) => {
      await callRpcResult("set_listing_template_override", {
        p_org_id: orgId,
        p_listing_id: listingId,
        p_template_id: templateId,
      });
      await queryClient.invalidateQueries({ queryKey: ["listing-template-override", orgId, listingId] });
    },
    [orgId, listingId, queryClient],
  );

  return { override, set, isLoading };
}

/**
 * Resolve the effective template for a listing server-side (listing override →
 * connector default → platform default), matching the publication pipeline.
 */
export function useResolvedListingTemplate(orgId: string | null | undefined, listingId: string | null | undefined) {
  return useQuery({
    queryKey: ["resolved-listing-template", orgId, listingId],
    enabled: !!orgId && !!listingId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("resolve_listing_template", {
        p_org_id: orgId,
        p_listing_id: listingId,
      });
      if (error) throw error;
      return (data as {
        template_id: string | null;
        source: "listing_override" | "connector_default" | "platform_default";
        provider: string | null;
      }) ?? { template_id: null, source: "platform_default", provider: null };
    },
  });
}